import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Dialog from "../../../../../../common/element/Dialog";
import Select from "../../../../../../common/element/Select";
import SweetAlertService from '../../../../../../common/SweetAlert/SweetAlert';
import { getSessionMasterList } from "../../../../../../redux/SessionMaster/sessionMasterSlice";
import { selectSessionMasterList } from "../../../../../../redux/SessionMaster/sessionMasterSelector";
import {
    addSalesTrainingSession,
    clearAddSalesTrainingSession
} from "../../../../../../redux/SalesTrainingCompanies/SalesTrainingCompaniesslice";
import {
    selectAddSalesTrainingSessionLoading,
    selectAddSalesTrainingSessionError,
    selectAddSalesTrainingSessionData
} from "../../../../../../redux/SalesTrainingCompanies/SalesTrainingCompaniesselectors";

const AddNewSession = ({ dialogOpen, onDialogClose, onSessionAdded }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const salesTrainingId = searchParams.get('id');
    const selectSessionMasterListData = useSelector(selectSessionMasterList);
    const addSessionLoading = useSelector(selectAddSalesTrainingSessionLoading);
    const addSessionError = useSelector(selectAddSalesTrainingSessionError);
    const addSessionData = useSelector(selectAddSalesTrainingSessionData);

    const [formData, setFormData] = useState({ sessionName: null, date: "", time: "" });
    const [errors, setErrors] = useState({});

    const sessionOptions = useMemo(() => selectSessionMasterListData?.map(session => ({
        value: session.id || session.session_id,
        label: session.name || session.session_name || session.title
    })) || [], [selectSessionMasterListData]);

    useEffect(() => {
        dispatch(getSessionMasterList({ page: 0, limit: 0, archived: false, access_group_id: 6 }));
    }, [dispatch]);

    useEffect(() => {
        if (addSessionData && !addSessionLoading) {
            setTimeout(() => {
                SweetAlertService.showSuccess({
                    title: 'Success!',
                    message: addSessionData?.message || 'Session added successfully',
                    onConfirm: () => {
                        dispatch(clearAddSalesTrainingSession());
                        if (onSessionAdded) onSessionAdded();
                        onDialogClose();
                    }
                });
            }, 200);
        }
    }, [addSessionData, addSessionLoading, onSessionAdded, onDialogClose, dispatch]);

    useEffect(() => {
        if (addSessionError) {
            SweetAlertService.showError({ title: 'Error!', message: addSessionError || 'Failed to add session' });
        }
    }, [addSessionError]);

    useEffect(() => {
        if (!dialogOpen) {
            setFormData({ sessionName: null, date: "", time: "" });
            setErrors({});
            dispatch(clearAddSalesTrainingSession());
        }
    }, [dialogOpen, dispatch]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    };

    const handleSubmit = () => {
        let newErrors = {};
        if (!formData.sessionName) newErrors.sessionName = "Please select a session";
        if (!formData.date) newErrors.date = "Please select a date";
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

        dispatch(addSalesTrainingSession({
            sales_training_company_id: parseInt(salesTrainingId),
            session_id: formData.sessionName.value,
            session_date: formData.date
        }));
    };

    const FormRow = ({ label, required, children }) => (
        <div className="flex items-start mb-6 last:mb-0">
            <label className="w-[30%] pt-2.5 font-lato font-medium text-[14px] leading-[150%] text-[#344054]">
                {label}{required && "*"}
            </label>
            <div className="w-[70%]">{children}</div>
        </div>
    );

    return (
        <Dialog isOpen={dialogOpen} onClose={onDialogClose} width={800} onRequestClose={onDialogClose} bodyOpenClassName="overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 border border-gray-200 rounded-lg shadow-sm bg-white">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                    </div>
                    <h5 className="font-lato font-medium text-[16px] text-[#101828]">Add New Session</h5>
                </div>
                <button onClick={onDialogClose} className="text-gray-400 hover:text-gray-600">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
            </div>

            <div className="px-6 py-10">
                <FormRow label="Session Name" required>
                    <Select className="w-full h-9 select-options" placeholder="Select Session" options={sessionOptions} value={formData.sessionName} onChange={(opt) => handleInputChange("sessionName", opt)} />
                    {errors.sessionName && <p className="text-xs text-red-600 mt-1">{errors.sessionName}</p>}
                </FormRow>

                <FormRow label="Date of Session" required>
                    <div className="relative w-full">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                        </div>
                        <input
                            type="date"
                            className={`w-full h-[38px] pl-10 pr-3 text-sm input input-shadow rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.date ? 'border-red-500' : 'border-gray-300'}`}
                            value={formData.date}
                            onChange={(e) => handleInputChange("date", e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    {errors.date && <p className="text-xs text-red-600 mt-1">{errors.date}</p>}
                </FormRow>
            </div>

            <div className="px-6 py-5 border-t border-gray-100 flex items-center gap-4">
                <button className="flex-1 h-11 bg-[#FFF4E5] text-[#101828] font-medium rounded-lg text-sm hover:bg-orange-100 transition-colors" onClick={onDialogClose} disabled={addSessionLoading}>
                    Cancel
                </button>
                <button onClick={handleSubmit} disabled={addSessionLoading} className="flex-1 h-11 bg-[#FF8E1C] text-white font-medium rounded-lg text-sm hover:bg-[#e68019] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {addSessionLoading ? "Adding..." : "Add Session"}
                </button>
            </div>
        </Dialog>
    );
};

export default AddNewSession;
